/**
 * @function createMarkerIcon
 * @description Builds a Google Maps marker icon (inline SVG data URL) for the given MEPBay
 * point type: a small green circle labeled "P"/"N" for perpendicular/new points, or a larger
 * layer-colored circle numbered 1-3 for the diffraction/coastal-control/predominant-wave/
 * auxiliary points.
 * @param {String} pointKey - Point type: "perpendicular", "newPoint", "auxiliaryPoint",
 * "diffractionPoint", "coastalControlPoint", or "predominantWavePoint".
 * @param {Object} layer - Layer the point belongs to, providing its `color`.
 * @param {Number} opacity - Marker opacity (0-1).
 * **Called by:
 * @see {LayerControler}
 * @see {ProcessingPage} - MEPBay module's processing page (imported as ProcessingMepBayPage).
 * @memberof module:Mepbay
 * @returns {Object|null} Google Maps icon descriptor, or null for an unrecognized pointKey.
 */
export const createMarkerIcon = (pointKey, layer, opacity) => {
  const anchor = new window.google.maps.Point(15, 15);

  if (pointKey === 'perpendicular') {
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8" fill="#90EE90" stroke="#ffffff" stroke-width="2" opacity="${opacity}"/>
          <text x="10" y="14" text-anchor="middle" fill="white" font-size="10" font-weight="bold" opacity="${opacity}">P</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(20, 20),
      anchor: new window.google.maps.Point(10, 10),
    };
  } else if (pointKey === 'newPoint') {
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8" fill="#90EE90" stroke="#ffffff" stroke-width="2" opacity="${opacity}"/>
          <text x="10" y="14" text-anchor="middle" fill="white" font-size="10" font-weight="bold" opacity="${opacity}">N</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(20, 20),
      anchor: new window.google.maps.Point(10, 10),
    };
  } else if (pointKey === 'auxiliaryPoint') {
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
          <circle cx="15" cy="15" r="12" fill="${layer.color}" stroke="#ffffff" stroke-width="2" opacity="${opacity}"/>
          <text x="15" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold" opacity="${opacity}">3</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: anchor,
    };
  } else if (
    pointKey === 'diffractionPoint' ||
    pointKey === 'coastalControlPoint' ||
    pointKey === 'predominantWavePoint'
  ) {
    const stepIndex = pointKey === 'diffractionPoint' ? 0 : pointKey === 'coastalControlPoint' ? 1 : 2;
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
          <circle cx="15" cy="15" r="12" fill="${layer.color}" stroke="#ffffff" stroke-width="2" opacity="${opacity}"/>
          <text x="15" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold" opacity="${opacity}">${stepIndex + 1}</text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(30, 30),
      anchor: anchor,
    };
  }
  return null;
};

/**
 * @function truncateDecimals
 * @description Truncates (rather than rounds) a number to the given number of decimal places.
 * @param {Number} num - Value to truncate.
 * @param {Number} decimals - Number of decimal places to keep.
 * **Called by:
 * @see {drawAngleAndR0}
 * @see {SidePanel}
 * @memberof module:Mepbay
 * @returns {Number} The truncated value.
 */
export const truncateDecimals = (num, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.trunc(num * factor) / factor;
};
